import { useState, useMemo } from 'react';

import { isUndefined } from 'lodash'

import {
  type ChecklistManagerOptions,
  type ChecklistManagerContext,
  type IChecklistManagerItemRefsState,
  type IChecklistManagerItemsState,
} from './types'

function itemsToArray<T>(
  itemsObj: IChecklistManagerItemsState = {},
  itemsRefs: IChecklistManagerItemRefsState<T> = {}
): T[] {
  return Object.keys(itemsObj)
    .filter((key) => itemsObj[key])
    .map((key) => itemsRefs[key])
}

export function useChecklistManager<ItemType extends Record<string, any>>({
  keyId = 'id',
  initialList = [],
  onChange = () => undefined,
}: ChecklistManagerOptions<ItemType> = {}): ChecklistManagerContext<ItemType> {
  const initialItems: IChecklistManagerItemsState = initialList.reduce(
    (prev, item) => {
      return {
        ...prev,
        [item[keyId]]: true,
      }
    },
    {}
  )

  const initialItemRefs: IChecklistManagerItemRefsState<ItemType> =
    initialList.reduce((prev, item) => {
      return {
        ...prev,
        [item[keyId]]: item,
      }
    }, {})

  const initialChecklist = itemsToArray<ItemType>(initialItems, initialItemRefs)

  const [checklist, setChecklist] = useState(initialChecklist)
  const [items, setItems] = useState(initialItems)
  const [itemRefs, setItemRefs] = useState(initialItemRefs)

  const check = (checkedItems: ItemType | ItemType[], checked?: boolean) => {
    let itemGroup = checkedItems

    const newItems: IChecklistManagerItemsState = {}
    const newItemRefs: IChecklistManagerItemRefsState<ItemType> = {}

    if (!(itemGroup instanceof Array)) {
      itemGroup = [itemGroup]
    }

    const ids = itemGroup.map((item) => {
      newItemRefs[item[keyId]] = item

      return item[keyId]
    })

    ids.forEach((id) => {
      newItems[id] = !isUndefined(checked) ? checked : !items[id]
    })

    const finalItems = {
      ...items,
      ...newItems,
    }

    const finalItemRefs = {
      ...itemRefs,
      ...newItemRefs,
    }

    const finalChecklist = itemsToArray<ItemType>(finalItems, finalItemRefs)

    setItems(finalItems)
    setItemRefs(finalItemRefs)
    setChecklist(finalChecklist)

    onChange(finalChecklist)
  }

  const clear = () => {
    setItems({})
    setItemRefs({})
    setChecklist([])

    onChange([])
  }

  const checklistState = useMemo(
    () => ({ items, itemRefs }),
    [items, itemRefs]
  )

  return {
    checklistState,
    checklist,
    check,
    clear,
  }
}
